import cache from '../../../cache/cache.js';
import { VIEWPORT_PRESETS } from '../../../constants/index.js';
import { Events } from '../../../enums/index.js';
import applyPreset from '../../../utilities/applyPreset.js';
import triggerEvent from '../../../utilities/triggerEvent.js';
import genericViewportDisplaySetMetadataProvider from '../../../utilities/genericViewportDisplaySetMetadataProvider.js';
import { viewportProjection } from '../viewportProjection.js';
import VolumeViewport3D from './viewport3D.js';
class VolumeViewport3DLegacyAdapter extends VolumeViewport3D {
    constructor() {
        super(...arguments);
        this.managedDataIds = new Set();
        this.volumeDataIds = new Map();
        this.legacyProperties = new Map();
        this.defaultLegacyProperties = new Map();
    }
    getCamera() {
        return this.getViewState();
    }
    setCamera(cameraPatch) {
        this.setViewState(cameraPatch);
    }
    getViewPresentation() {
        return viewportProjection.getPresentation(this);
    }
    setViewPresentation(viewPresentation) {
        if (!viewPresentation) {
            return;
        }
        const presentation = isVolume3DViewPresentation(viewPresentation)
            ? viewPresentation
            : { camera: viewPresentation };
        const nextCamera = viewportProjection.withPresentation(this, presentation);
        if (nextCamera) {
            this.setViewState(nextCamera);
        }
    }
    resetCamera() {
        return this.resetViewState();
    }
    resetCameraForResize() {
        return this.resetViewStateForResize();
    }
    async setVolumes(volumeInputArray, immediate = false, suppressEvents = false) {
        await this.mountVolumes(volumeInputArray, true, suppressEvents);
        if (immediate) {
            this.render();
        }
    }
    async addVolumes(volumeInputArray, immediate = false, suppressEvents = false) {
        await this.mountVolumes(volumeInputArray, false, suppressEvents);
        if (immediate) {
            this.render();
        }
    }
    setProperties(properties = {}, volumeId, suppressEvents = false) {
        const targetDataId = this.resolveTargetDataId(volumeId);
        if (!targetDataId) {
            return;
        }
        const nextProperties = {
            ...(this.legacyProperties.get(targetDataId) || {}),
            ...properties,
        };
        if (!this.defaultLegacyProperties.has(targetDataId)) {
            this.defaultLegacyProperties.set(targetDataId, { ...properties });
        }
        this.legacyProperties.set(targetDataId, nextProperties);
        const presentationPatch = {};
        if (properties.voiRange !== undefined) {
            presentationPatch.voiRange = properties.voiRange;
        }
        if (properties.invert !== undefined) {
            presentationPatch.invert = properties.invert;
        }
        if (properties.interpolationType !== undefined) {
            presentationPatch.interpolationType = properties.interpolationType;
        }
        if (properties.sampleDistanceMultiplier !== undefined) {
            presentationPatch.sampleDistanceMultiplier =
                properties.sampleDistanceMultiplier;
        }
        if (Object.keys(presentationPatch).length) {
            this.setDisplaySetPresentation(targetDataId, presentationPatch);
        }
        if (properties.preset !== undefined) {
            this.applyPresetToBinding(targetDataId, properties.preset, volumeId, suppressEvents);
        }
        this.render();
    }
    getProperties(volumeId) {
        const targetDataId = this.resolveTargetDataId(volumeId);
        if (!targetDataId) {
            return {};
        }
        return { ...(this.legacyProperties.get(targetDataId) || {}) };
    }
    resetProperties(volumeId) {
        const targetDataId = this.resolveTargetDataId(volumeId);
        if (!targetDataId) {
            return;
        }
        const defaults = this.defaultLegacyProperties.get(targetDataId) || {};
        this.legacyProperties.set(targetDataId, { ...defaults });
        this.setDisplaySetPresentation(targetDataId, {
            voiRange: defaults.voiRange,
            invert: defaults.invert,
            interpolationType: defaults.interpolationType,
            sampleDistanceMultiplier: defaults.sampleDistanceMultiplier,
        });
        if (defaults.preset !== undefined) {
            this.applyPresetToBinding(targetDataId, defaults.preset, volumeId, false);
        }
        this.render();
    }
    setSampleDistanceMultiplier(multiplier) {
        this.setProperties({ sampleDistanceMultiplier: multiplier });
    }
    removeData(dataId) {
        super.removeData(dataId);
        this.forgetData(dataId);
    }
    getActors() {
        return super.getActors();
    }
    getDefaultActor() {
        return super.getDefaultActor();
    }
    async mountVolumes(volumeInputArray, replaceExisting, suppressEvents) {
        if (!volumeInputArray.length) {
            return;
        }
        const dataIds = [];
        try {
            for (const volumeInput of volumeInputArray) {
                const cachedVolume = cache.getVolume(volumeInput.volumeId);
                if (!cachedVolume) {
                    throw new Error(`imageVolume with id: ${volumeInput.volumeId} does not exist, you need to create/allocate the volume first`);
                }
                const dataId = this.getLegacyVolumeDataId(volumeInput.volumeId);
                const dataSet = {
                    imageIds: cachedVolume.imageIds,
                    volumeId: volumeInput.volumeId,
                };
                genericViewportDisplaySetMetadataProvider.add(dataId, dataSet);
                this.managedDataIds.add(dataId);
                this.volumeDataIds.set(volumeInput.volumeId, dataId);
                dataIds.push(dataId);
            }
            if (replaceExisting) {
                this.removeBindingsExcept(new Set(dataIds));
            }
            await this.setDisplaySets(...dataIds.map((dataId) => ({
                displaySetId: dataId,
                options: { renderMode: 'vtkVolume3d' },
            })));
            volumeInputArray.forEach((volumeInput, index) => {
                const dataId = dataIds[index];
                if (volumeInput.visibility !== undefined) {
                    this.setDisplaySetPresentation(dataId, {
                        visible: volumeInput.visibility,
                    });
                }
                if (volumeInput.callback) {
                    const actor = this.getBindingActor(dataId);
                    if (actor) {
                        volumeInput.callback({
                            volumeActor: actor,
                            volumeId: volumeInput.volumeId,
                        });
                    }
                }
            });
            if (!suppressEvents) {
                triggerEvent(this.element, Events.VOLUME_VIEWPORT_NEW_VOLUME, {
                    viewportId: this.id,
                    volumeActors: this.buildActorEntries(volumeInputArray, dataIds),
                });
            }
        }
        catch (error) {
            dataIds.forEach((dataId) => {
                this.forgetData(dataId);
            });
            throw error;
        }
    }
    buildActorEntries(volumeInputArray, dataIds) {
        return volumeInputArray.flatMap((volumeInput, index) => {
            const actor = this.getBindingActor(dataIds[index]);
            if (!actor) {
                return [];
            }
            const { actorUID, slabThickness, volumeId, ...rest } = volumeInput;
            return [
                {
                    uid: actorUID || volumeId,
                    actor: actor,
                    slabThickness,
                    referencedId: volumeId,
                    ...rest,
                },
            ];
        });
    }
    applyPresetToBinding(dataId, presetNameOrObj, requestedVolumeId, suppressEvents) {
        const preset = typeof presetNameOrObj === 'string'
            ? VIEWPORT_PRESETS.find((entry) => entry.name === presetNameOrObj)
            : presetNameOrObj;
        if (!preset) {
            return;
        }
        const actor = this.getBindingActor(dataId);
        if (!actor) {
            return;
        }
        applyPreset(actor, preset);
        if (!suppressEvents) {
            triggerEvent(this.element, Events.PRESET_MODIFIED, {
                viewportId: this.id,
                volumeId: requestedVolumeId ?? this.resolveVolumeIdForDataId(dataId),
                actor,
                presetName: preset.name,
            });
        }
    }
    getBindingActor(dataId) {
        const binding = this.getBinding(dataId);
        if (!binding) {
            return;
        }
        const rendering = binding.rendering;
        return rendering?.actor;
    }
    resolveTargetDataId(volumeId) {
        if (volumeId) {
            const mapped = this.volumeDataIds.get(volumeId);
            if (mapped) {
                return mapped;
            }
            for (const [dataId, binding] of this.bindings.entries()) {
                const rendering = binding.rendering;
                if (rendering?.imageVolume?.volumeId === volumeId) {
                    return dataId;
                }
            }
        }
        return this.bindings.keys().next().value;
    }
    resolveVolumeIdForDataId(dataId) {
        for (const [volumeId, mappedDataId] of this.volumeDataIds.entries()) {
            if (mappedDataId === dataId) {
                return volumeId;
            }
        }
    }
    removeBindingsExcept(keepDataIds) {
        for (const dataId of Array.from(this.bindings.keys())) {
            if (!keepDataIds.has(dataId)) {
                this.removeData(dataId);
            }
        }
    }
    getLegacyVolumeDataId(volumeId) {
        return `__volume3d__:${this.id}:volume:${volumeId}`;
    }
    forgetData(dataId) {
        if (!this.managedDataIds.delete(dataId)) {
            return;
        }
        genericViewportDisplaySetMetadataProvider.remove(dataId);
        this.legacyProperties.delete(dataId);
        this.defaultLegacyProperties.delete(dataId);
        for (const [volumeId, mappedDataId] of this.volumeDataIds.entries()) {
            if (mappedDataId === dataId) {
                this.volumeDataIds.delete(volumeId);
            }
        }
    }
    onDestroy() {
        for (const dataId of Array.from(this.managedDataIds)) {
            genericViewportDisplaySetMetadataProvider.remove(dataId);
        }
        this.managedDataIds.clear();
        this.volumeDataIds.clear();
        this.legacyProperties.clear();
        this.defaultLegacyProperties.clear();
        super.onDestroy();
    }
}
export default VolumeViewport3DLegacyAdapter;
function isVolume3DViewPresentation(viewPresentation) {
    return (Boolean(viewPresentation) &&
        typeof viewPresentation === 'object' &&
        'camera' in viewPresentation);
}
